import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { playClickSound } from '@/utils/soundUtils';

export interface IosPwaGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function IosPwaGuideModal({
  isOpen,
  onClose,
}: IosPwaGuideModalProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const option3HtmlUrl = `${import.meta.env.BASE_URL}ios_guide/option-3-dark-premium.html`;

  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-3 sm:p-4 md:p-6" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        aria-label={t('supportRail.closeIosGuideAriaLabel')}
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-[min(100vw-24px,1180px)] rounded-[24px] border border-[#30466f] bg-[linear-gradient(180deg,rgba(8,19,43,0.96)_0%,rgba(9,18,37,0.98)_100%)] p-3 md:p-4 shadow-[0_28px_70px_rgba(0,0,0,0.58),0_0_0_1px_rgba(85,132,226,0.2)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between gap-3 px-1">
          <h2 id={titleId} className="text-sm md:text-base font-semibold text-white/85 leading-tight">
            {t('supportRail.iosGuide.title')}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/15 text-white/80 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-high-light,#22d3ee)]"
            aria-label={t('supportRail.closeIosGuideAriaLabel')}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <iframe
          src={option3HtmlUrl}
          title={t('supportRail.iosGuide.title')}
          className="block h-[min(84vh,860px)] w-full rounded-[18px] border border-white/12 bg-[#0b1220]"
        />
      </div>
    </div>
  );
}
